import { useMemo } from "react";
import moment from "moment-timezone";
import { ProgramPhaseList } from "./ProgramPhaseList";
import { ProgramPhase } from "@platform/types/platform.types";
import { usePlatform } from "@platform/PlatformContext";
import { useProgram } from "../../hooks/useProgram";

export type Props = {
  selectedPhase: ProgramPhase | undefined;
  setSelectedPhase: (phase: ProgramPhase) => void;
  phaseContainerRef: React.RefObject<HTMLDivElement>;
  onPhaseListReady: () => void;
  isLoading: boolean;
};

export function ProgramPhaseListContainer({
  selectedPhase,
  setSelectedPhase,
  phaseContainerRef,
  onPhaseListReady,
  isLoading,
}: Props) {
  const { userPhaseActions } = usePlatform();

  const { currentPhase, phases } = useProgram();

  const phasesWithActions = useMemo(() => {
    if (!userPhaseActions) return phases; // Preserve existing `actions`

    return phases.map((phase) => {
      const actions = userPhaseActions
        ?.filter((action) => action.program_phase.id === phase.id)
        .sort((a, b) => {
          // First sort by available_date
          const dateDiff = moment(a.available_date).diff(
            moment(b.available_date)
          );
          if (dateDiff !== 0) {
            return dateDiff;
          }
          // Then sort by type as secondary criterion
          return a.type.localeCompare(b.type);
        });
      return { ...phase, actions: actions || [] };
    });
  }, [phases, userPhaseActions]);

  if (phases.length === 0) {
    return null;
  }

  return (
    <ProgramPhaseList
      phases={phasesWithActions}
      currentPhase={currentPhase || (phases[0] as ProgramPhase)}
      selectedPhase={selectedPhase}
      setSelectedPhase={setSelectedPhase}
      phaseContainerRef={phaseContainerRef}
      onPhaseListReady={onPhaseListReady}
      isLoading={isLoading}
    />
  );
}

export default ProgramPhaseListContainer;
